import { useCallback, useEffect, useRef, useState } from 'react';
import { useOnElementResize } from '@/hooks/useOnElementResize';

function getDimension(element: HTMLElement | null) {
  if (element) {
    const { width, height } = element.getBoundingClientRect();
    return { width, height };
  }

  return { width: -1, height: -1 };
}

function useDimensionObserver() {
  const [dimension, setDimension] = useState(() => getDimension(null));
  const updateDimension = useCallback((element: HTMLElement | null) => {
    setDimension(getDimension(element));
  }, []);
  const callbackRef = useOnElementResize(updateDimension);
  return [callbackRef, dimension] as const;
}

/**
 * Fallback for browsers without ResizeObserver
 *  obviously not a perfect polyfill but enough for our current use cases
 */
function useDimensionFallback() {
  const ref = useRef<HTMLElement | null>(null);
  const setRef = useCallback((element: HTMLElement | null) => (ref.current = element), []);

  const [dimension, setDimension] = useState(() => getDimension(null));
  const dimensionRef = useRef(dimension);

  const handler = useCallback(() => {
    const currentDimension = dimensionRef.current;
    const newDimension = getDimension(ref.current);
    if (
      newDimension.width !== currentDimension.width ||
      newDimension.height !== currentDimension.height
    ) {
      dimensionRef.current = newDimension;
      setDimension(newDimension);
    }
  }, [setDimension]);

  useEffect(() => {
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [handler]);
  useEffect(handler); // handler needs to be called on every render

  return [setRef, dimension] as const;
}

export const useDimension = window.ResizeObserver ? useDimensionObserver : useDimensionFallback;
